import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, Spin, Tag } from "antd";
import { CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import "./tags-multi-select.scss";
import Asterik from "../../../views/internal/shared/helper-components/Asterik";
import { clearTags, getMoreTags, getTags } from "../../../redux/tags/actions";
import TagsModal from "./TagsModal";
const PAGE_SIZE_DEFAULT = 10;
const SELECT_MIN_HEIGHT = 32;
const TagsMultiSelect = ({ name, value = [], onChange, label = "Associate Tag", disabled, sizePerPage = PAGE_SIZE_DEFAULT, className, }) => {
    const dispatch = useDispatch();
    const { items = [], page = 0, size = sizePerPage, last = false } = useSelector((s) => { var _a; return (_a = s === null || s === void 0 ? void 0 : s.tags) !== null && _a !== void 0 ? _a : {}; });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectLoading, setSelectLoading] = useState(false);
    const [searchRaw, setSearchRaw] = useState("");
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSearchRaw, setModalSearchRaw] = useState("");
    const [modalSearch, setModalSearch] = useState("");
    const [selectFocused, setSelectFocused] = useState(false);
    useEffect(() => {
        setSelectLoading(true);
        dispatch(clearTags());
        dispatch(getTags({ page: 0, size: sizePerPage, search: "" })).finally(() => setSelectLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const id = setTimeout(() => setSearch(searchRaw === null || searchRaw === void 0 ? void 0 : searchRaw.trim()), 300);
        return () => clearTimeout(id);
    }, [searchRaw]);
    useEffect(() => {
        const id = setTimeout(() => setModalSearch(modalSearchRaw === null || modalSearchRaw === void 0 ? void 0 : modalSearchRaw.trim()), 200);
        return () => clearTimeout(id);
    }, [modalSearchRaw]);
    const selectedIds = useMemo(() => new Set(value === null || value === void 0 ? void 0 : value.map((t) => t === null || t === void 0 ? void 0 : t.tagId)), [value]);
    const byId = useMemo(() => {
        const map = new Map();
        value === null || value === void 0 ? void 0 : value.forEach((t) => map.set(t === null || t === void 0 ? void 0 : t.tagId, t));
        items === null || items === void 0 ? void 0 : items.forEach((t) => map === null || map === void 0 ? void 0 : map.set(t === null || t === void 0 ? void 0 : t.tagId, t));
        return map;
    }, [items, value]);
    useEffect(() => {
        if (!dropdownOpen)
            return;
        setSelectLoading(true);
        dispatch(clearTags());
        dispatch(getTags({ page: 0, size: sizePerPage, search })).finally(() => setSelectLoading(false));
    }, [dropdownOpen, search, sizePerPage, dispatch]);
    const handlePopupScroll = (e) => {
        const target = e === null || e === void 0 ? void 0 : e.target;
        if (!target)
            return;
        const nearBottom = (target === null || target === void 0 ? void 0 : target.scrollTop) + (target === null || target === void 0 ? void 0 : target.clientHeight) >= (target === null || target === void 0 ? void 0 : target.scrollHeight) - 24;
        if (nearBottom && !last && !selectLoading) {
            setSelectLoading(true);
            dispatch(getMoreTags({
                page: (page !== null && page !== void 0 ? page : 0) + 1,
                size: size !== null && size !== void 0 ? size : sizePerPage,
                search,
            })).finally(() => setSelectLoading(false));
        }
    };
    const reloadDefault = () => {
        setSearchRaw("");
        setSearch("");
        setSelectLoading(true);
        dispatch(clearTags());
        dispatch(getTags({ page: 0, size: sizePerPage, search: "" })).finally(() => setSelectLoading(false));
    };
    const resetSearch = () => {
        setSearchRaw("");
        setSearch("");
    };
    const handleSelectChange = (ids) => {
        const next = [];
        const seen = new Set();
        ids.forEach((id) => {
            if (seen === null || seen === void 0 ? void 0 : seen.has(id))
                return;
            const obj = byId === null || byId === void 0 ? void 0 : byId.get(id);
            if (obj) {
                seen === null || seen === void 0 ? void 0 : seen.add(id);
                next === null || next === void 0 ? void 0 : next.push(obj);
            }
        });
        onChange(next);
    };
    const removeTag = (id) => onChange(value === null || value === void 0 ? void 0 : value.filter((t) => (t === null || t === void 0 ? void 0 : t.tagId) !== id));
    const clearAll = () => onChange([]);
    const showShowMore = (value === null || value === void 0 ? void 0 : value.length) > 7;
    const visibleChips = value === null || value === void 0 ? void 0 : value.slice(0, 7);
    const filteredSelected = useMemo(() => {
        if (!modalSearch)
            return value;
        const q = modalSearch.toLowerCase();
        return value === null || value === void 0 ? void 0 : value.filter((t) => { var _a, _b; return (_b = (_a = t === null || t === void 0 ? void 0 : t.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === null || _b === void 0 ? void 0 : _b.includes(q); });
    }, [value, modalSearch]);
    return (React.createElement("div", { className: className || "", style: { marginBottom: 20 } },
        React.createElement("div", { className: "ant-col ant-form-item-label" },
            React.createElement("label", null,
                label,
                " ",
                React.createElement(Asterik, null))),
        React.createElement("div", { className: "row gx-3 align-items-start" },
            React.createElement("div", { className: "col-md-4 order-1 mb-3" },
                React.createElement("div", { className: `tags-select-with-reload ${selectFocused ? "is-focused" : ""}` },
                    React.createElement(Select, { mode: "multiple", disabled: disabled, className: "w-100", placeholder: selectFocused ? "" : "Search and Select", searchValue: searchRaw, value: value.map((v) => v === null || v === void 0 ? void 0 : v.tagId), onChange: handleSelectChange, onSelect: () => resetSearch(), showSearch: true, filterOption: false, onSearch: (val) => setSearchRaw(val), onDropdownVisibleChange: (v) => setDropdownOpen(v), onPopupScroll: handlePopupScroll, tagRender: () => null, maxTagCount: 0, maxTagPlaceholder: (omittedValues) => {
                            var _a, _b;
                            const label = `${(_b = (_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : omittedValues === null || omittedValues === void 0 ? void 0 : omittedValues.length) !== null && _b !== void 0 ? _b : 0} selected`;
                            return (React.createElement("span", { className: "tags-select-count", title: label }, label));
                        }, loading: selectLoading, listHeight: 256, dropdownMatchSelectWidth: true, virtual: false, getPopupContainer: (trigger) => trigger === null || trigger === void 0 ? void 0 : trigger.parentElement, dropdownClassName: "tags-multiselect-dropdown", onFocus: () => setSelectFocused(true), onBlur: () => setSelectFocused(false), notFoundContent: selectLoading ? (React.createElement("div", { style: { padding: 16, display: "flex", justifyContent: "center" } },
                            React.createElement(Spin, null))) : ("No tags"), dropdownRender: (menu) => (React.createElement("div", { style: { position: "relative", width: "100%" } },
                            menu,
                            selectLoading && (items === null || items === void 0 ? void 0 : items.length) > 0 && (React.createElement("div", { style: {
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 8,
                                    background: "rgba(255,255,255,0.75)",
                                } },
                                React.createElement(Spin, null))))), options: items === null || items === void 0 ? void 0 : items.map((opt) => {
                            const isSelected = selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds.has(opt.tagId);
                            return {
                                value: opt === null || opt === void 0 ? void 0 : opt.tagId,
                                label: (React.createElement("div", { style: { display: "flex", alignItems: "flex-start" } },
                                    React.createElement("div", { style: { width: 18, marginRight: 8, marginTop: 2 } }, isSelected && React.createElement(CheckOutlined, null)),
                                    React.createElement("div", null,
                                        React.createElement("div", { style: { fontWeight: 500 } }, opt === null || opt === void 0 ? void 0 : opt.name)))),
                            };
                        }) }),
                    selectFocused && (React.createElement("button", { type: "button", className: "tags-reload-button", onMouseDown: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }, onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            reloadDefault();
                        }, "aria-label": "Reload tags", title: "Reload tags" },
                        React.createElement(ReloadOutlined, { spin: selectLoading }))))),
            React.createElement("div", { className: "col-md-8 order-2" },
                React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 8, minHeight: SELECT_MIN_HEIGHT } }, visibleChips === null || visibleChips === void 0 ? void 0 : visibleChips.map((t) => (React.createElement(Tag, { key: t.tagId, closable: !disabled, color: "default", style: { background: "#fff", color: "#000", border: "1px solid #d9d9d9" }, closeIcon: React.createElement(CloseOutlined, { style: { color: "#ff4d4f" } }), onClose: () => removeTag(t === null || t === void 0 ? void 0 : t.tagId) }, t === null || t === void 0 ? void 0 : t.name)))),
                (value === null || value === void 0 ? void 0 : value.length) > 0 && (React.createElement("div", { className: "d-flex justify-content-end align-items-center", style: { gap: 8, marginTop: 12 } },
                    showShowMore && (React.createElement(Button, { size: "small", type: "link", className: "p-0", onClick: () => setModalOpen(true), disabled: disabled }, "Show more")),
                    !disabled && (React.createElement(Button, { size: "small", onClick: clearAll, style: { background: "#ff4d4f", color: "#fff", border: "none", boxShadow: "0 2px 8px rgba(255,77,79,0.45)" } }, "Clear all")),
                    React.createElement("span", { style: { fontWeight: 500, marginLeft: 8 } },
                        value.length,
                        " selected"))),
                showShowMore && (React.createElement("div", { style: { marginTop: 10, fontSize: 12, color: "rgba(0,0,0,0.45)", textAlign: "right" } },
                    "Click ",
                    React.createElement("strong", null, "Show more"),
                    " to view all selected tags.")))),
        React.createElement(TagsModal, { open: modalOpen, onClose: () => setModalOpen(false), tags: value, searchValue: modalSearchRaw, onSearchChange: (val) => setModalSearchRaw(val), filteredTags: filteredSelected, onRemoveTag: removeTag, isTagCloseable: !disabled })));
};
export default TagsMultiSelect;
